import type { Card } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

// 7 tableau columns, 5 face-up cards each (35), one face-up foundation seed,
// the remaining 16 face-down in the stock.
export const TABLEAU_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7'] as const;
export type TableauId = (typeof TABLEAU_IDS)[number];

const COLUMN_HEIGHT = 5;

// Classic Golf: Ace is low, King is high, no round-the-corner wrap. A card can
// only move onto the foundation if it is exactly one rank away from the top.
function rankAdjacent(a: number, b: number): boolean {
  return Math.abs(a - b) === 1;
}

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

function setupGolf(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of TABLEAU_IDS) piles[id] = emptyPile(id, 'tableau');
  piles.stock = emptyPile('stock', 'stock');
  piles.foundation = emptyPile('foundation', 'foundation');

  const d = deck.slice();
  // 5 rows dealt left-to-right across the 7 columns, all face-up.
  for (let row = 0; row < COLUMN_HEIGHT; row++) {
    for (const id of TABLEAU_IDS) {
      const c = d.shift()!;
      c.faceUp = true;
      piles[id]!.cards.push(c);
    }
  }
  // Seed the foundation with one face-up card so play can begin immediately
  // (mirrors TriPeaks seeding the waste).
  const seed = d.shift()!;
  seed.faceUp = true;
  piles.foundation!.cards.push(seed);
  // Remaining 16 → stock, face-down.
  for (const c of d) {
    c.faceUp = false;
    piles.stock!.cards.push(c);
  }
  return { piles, movesCount: 0 };
}

export const golfRules: SolitaireRules = {
  id: 'golf',

  setup(deck) {
    return setupGolf(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to) return false;
    if (from === to) return false;

    // The foundation is the only legal destination.
    if (to.id !== 'foundation') return false;

    // Stock → foundation: deal the top stock card (always allowed).
    if (from.type === 'stock') {
      const top = from.cards[from.cards.length - 1];
      return !!top && top.id === move.cardId;
    }

    // Tableau → foundation: only the exposed (last) card, rank ±1 from the
    // foundation top.
    if (from.type !== 'tableau') return false;
    const card = from.cards[from.cards.length - 1];
    if (!card || card.id !== move.cardId) return false;
    const fTop = to.cards[to.cards.length - 1];
    if (!fTop) return false;
    return rankAdjacent(card.rank, fTop.rank);
  },

  isWon(state) {
    for (const id of TABLEAU_IDS) {
      if ((state.piles[id]?.cards.length ?? 0) > 0) return false;
    }
    return true;
  },

  isLost(state) {
    if (state.piles.stock!.cards.length > 0) return false;
    if (hasAnyMatch(state)) return false;
    // Stock empty, no playable tableau card, but columns remain → unwinnable.
    for (const id of TABLEAU_IDS) {
      if ((state.piles[id]?.cards.length ?? 0) > 0) return true;
    }
    return false;
  },
};

export function drawFromStock(state: GameState): boolean {
  const stock = state.piles.stock!;
  const foundation = state.piles.foundation!;
  if (stock.cards.length === 0) return false;
  const c = stock.cards.pop()!;
  c.faceUp = true;
  foundation.cards.push(c);
  return true;
}

export function hasAnyMatch(state: GameState): boolean {
  const fCards = state.piles.foundation!.cards;
  const fTop = fCards[fCards.length - 1];
  if (!fTop) return false;
  for (const id of TABLEAU_IDS) {
    const pile = state.piles[id];
    if (!pile || pile.cards.length === 0) continue;
    const top = pile.cards[pile.cards.length - 1]!;
    if (rankAdjacent(top.rank, fTop.rank)) return true;
  }
  return false;
}
